import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import * as uuid from 'uuid';
import * as fromUser from '@app/core/user/store/reducers';
import { Store, select } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { IAppState } from '@app/store';
import { User } from '@sentry/browser';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { Logger } from '@app/core/logging';
import * as sha256 from 'hash.js/lib/hash/sha/256';

// From https://www.algolia.com/doc/rest-api/insights/#push-events
interface IParameters {
  eventType: string;
  eventName: string;
  index: string;
  userToken: string;
  timestamp?: number;
  queryID?: string;
  objectIDs?: string[];
  filters?: string[];
  positions?: number[];
}

interface QueryParams {
  index: string;
  queryId?: string;
}

export interface ClickedProducts {
  [productId: string]: QueryParams;
}

@Injectable()
export class AlgoliaInsightService implements OnDestroy {
  private userToken = new BehaviorSubject(`anonymous-${uuid.v4()}`);
  private ngUnsubscribe = new Subject<void>();
  private clickedProducts: ClickedProducts = {};

  constructor(private http: HttpClient, private store: Store<IAppState>, private logger: Logger) {
    this.store.pipe(select(fromUser.getUser), takeUntil(this.ngUnsubscribe)).subscribe({
      next: (user: User) => user && this.userToken.next(this.createUserToken(user))
    });
  }

  private createUserToken(user: User): string {
    return user
      ? sha256()
          .update(user.id)
          .digest('hex')
      : `anonymous-${uuid.v4()}`;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  addClickedProduct(productId: string, indexName: string, queryID: string): void {
    this.clickedProducts[productId] = { index: indexName, queryId: queryID };
  }

  clickAfterSearch(queryID: string, index: string, positions: number[], objectIDs: string[]) {
    this.postInsight({
      eventType: 'click',
      eventName: 'clickAfterSearch',
      userToken: this.userToken.getValue(),
      index: index,
      queryID: queryID,
      objectIDs: objectIDs,
      positions: positions
    });
  }

  clickFilter(index: string, event) {
    const isSize = event.filterOptions.facet.includes('size.facet');
    const isGender = event.filterItem.id === 2 || event.filterItem.id === 1;

    if (!isSize && !isGender) {
      return;
    }

    const filter = isGender
      ? `universe.id:${event.filterItem.id}`
      : `${event.filterOptions.facet.split('.')[0]}.id:${event.filterItem.id}`;

    this.postInsight({
      eventType: 'click',
      eventName: isSize ? 'clickedSize' : isGender ? 'clickedUniverse' : undefined,
      index,
      userToken: this.userToken.getValue(),
      filters: [filter]
    });
  }

  addToCart(productId: string) {
    const params: IParameters = {
      eventType: 'conversion',
      eventName: 'convertedObjectIDsAfterSearch',
      userToken: this.userToken.getValue(),
      objectIDs: [productId],
      index: ''
    };
    if (!(productId in this.clickedProducts)) {
      return false;
    } else if (!this.clickedProducts[productId].queryId) {
      params.eventName = 'convertedObjectIds';
      params.index = 'ng-product-master';
      return this.postInsight({ ...params });
    }

    return this.postInsight({ ...params, ...this.clickedProducts[productId] });
  }

  getUserToken(): Observable<string> {
    return this.userToken.asObservable();
  }

  private postInsight(event: IParameters) {
    const params = `X-Algolia-Application-Id=${environment.algoliaAppId}&X-Algolia-API-Key=${environment.algoliaApiKey}`;
    const endpoint = 'https://insights.algolia.io/1/events';

    // @TODO: will bring back after release
    // if (environment.algoliaProxyHost) {
    //   endpoint = `https://${environment.algoliaProxyHost}/1/events`;
    // }

    this.http.post(`${endpoint}?${params}`, { events: [event] }).subscribe({
      next: _ => {},
      error: error => this.logger.error(`Error while sending algolia insight. Details ${JSON.stringify(error)}`)
    });
  }
}
